import type { ComponentType, ReactNode } from "react";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import Stack from "@mui/material/Stack";
import { useMemo } from "react";
import { PAGE_PADDING_X, PageSpinner, PageWrapper } from "../shared/Page";
import { Grid } from "../shared/Grid";
import {
  GridContext,
  HEIGHT,
  useActiveDrawerElement,
  DRAWER_WIDTH,
  useGetFilterByActiveTabValue,
  useOptionalSelectedRowId,
  type ToolbarTabs,
} from "./DynamicGrid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

type CardListTexts = { searchFieldPlaceholder?: string };

type CardListProps<RowData extends Record<string, unknown>, ToolbarTabValue extends string> = {
  /** "undefined" will show the loading state */
  rowsData: Array<RowData> | undefined;
  getRowId: (rowData: RowData) => string;
  card: (row: RowData) => { content: ReactNode; searchableValue: string; clickable?: boolean };
  Toolbar: ComponentType;
  NoRowsOverlay: ComponentType;
  onRowClick: (id: string) => void;
  texts?: CardListTexts;
  toolbarTabs?: ToolbarTabs<RowData, ToolbarTabValue>;
  /** Extra vertical offset (in px) to subtract when the page has content above the grid; it should match that content's total height. */
  extraHeightOffset?: number;
};

export const CardList = <RowData extends Record<string, unknown>, ToolbarTabValue extends string>({
  rowsData,
  getRowId,
  card,
  onRowClick,
  Toolbar,
  NoRowsOverlay,
  texts,
  toolbarTabs,
  extraHeightOffset,
}: CardListProps<RowData, ToolbarTabValue>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const activeDrawer = useActiveDrawerElement();
  const selectedRowId = useOptionalSelectedRowId();
  const filterByActiveTabValue = useGetFilterByActiveTabValue(toolbarTabs);
  const gridApiRef = useGridApiRef();

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: "include",
      ids: selectedRowId ? new Set([selectedRowId]) : new Set(),
    }),
    [selectedRowId]
  );

  const rows = rowsData ? filterByActiveTabValue(rowsData).map(rowData => ({ id: getRowId(rowData), ...rowData })) : [];
  const isLoading = !rowsData;

  const cardWidth = isMobile ? window.innerWidth - 48 : CARD_WIDTH;

  const pageWidth = activeDrawer ? `calc(100% - ${DRAWER_WIDTH})` : "100%";
  return (
    <PageWrapper sx={{ width: pageWidth, px: 0, pb: 0 }}>
      {isLoading ? (
        <PageSpinner />
      ) : (
        <GridContext
          value={{
            gridApiRef,
            texts,
            selectedRowId,
            toolbarTabsProps: toolbarTabs ? { queryKey: toolbarTabs.queryKey, tabs: toolbarTabs.tabs } : undefined,
          }}
        >
          <Stack>
            <Toolbar />
            <Stack
              direction="row"
              sx={{ mr: selectedRowId ? "-2px" /** To make to drawer overlap the grid a TINY bit */ : 0 }}
            >
              <Grid
                apiRef={gridApiRef}
                getRowHeight={() => "auto"}
                disableMultipleRowSelection
                disableRowSelectionExcludeModel
                onRowClick={params => {
                  if (card(params.row).clickable) onRowClick(String(params.id));
                }}
                rowSelectionModel={rowSelectionModel}
                columnHeaderHeight={0}
                slots={{
                  noRowsOverlay: NoRowsOverlay,
                  noResultsOverlay: NoRowsOverlay,
                  columnHeaders: HIDDEN_SLOT,
                  footer: HIDDEN_SLOT,
                }}
                rows={rows}
                partsSx={{
                  rowSx: {
                    width: cardWidth,
                    mt: 2,
                    mx: PAGE_PADDING_X,
                    border: "none",
                  },
                  cellSx: { p: 0 },
                }}
                columns={[
                  {
                    width: cardWidth,
                    field: "card",
                    renderCell: ({ row }) => card(row).content,
                    valueGetter: (_, row) => card(row).searchableValue,
                  },
                ]}
                sx={{ height: HEIGHT(extraHeightOffset) }}
              />
            </Stack>
          </Stack>
        </GridContext>
      )}
      {activeDrawer}
    </PageWrapper>
  );
};

const CARD_WIDTH = 600;
const HIDDEN_SLOT = () => null;
